import { TypedDocumentNode } from '@graphql-typed-document-node/core';

export interface IGqlOperationLogger {
  debug: typeof console.debug;
  info: typeof console.info;
  warn: typeof console.warn;
}

export interface IGqlOperation<
  Vars extends Record<string, unknown>,
  Data extends Record<string, unknown>,
> {
  readonly document: TypedDocumentNode<Data, Vars>;
  readonly operationName: string;
  request: (variables: Vars, options?: { signal?: AbortSignal }) => Promise<Data>;
}

export type RequestFn = <Vars, Data>(options: {
  document: TypedDocumentNode<Data, Vars>;
  variables: Vars;
  signal?: AbortSignal;
}) => Promise<Data>;

export class GqlOperation<
  Vars extends Record<string, unknown>,
  Data extends Record<string, unknown>,
> implements IGqlOperation<Vars, Data>
{
  private _logger: IGqlOperationLogger;
  private _requestFn: RequestFn;
  readonly document: TypedDocumentNode<Data, Vars>;
  readonly operationName: string;
  readonly canWorkWithShareLinks: boolean;

  constructor(args: {
    canWorkWithShareLinks: boolean;
    document: TypedDocumentNode<Data, Vars>;
    logger: IGqlOperationLogger;
    operationName: string;
    requestFn: RequestFn;
  }) {
    this._logger = args.logger;
    this._requestFn = args.requestFn;
    this.document = args.document;
    this.operationName = args.operationName;
    this.canWorkWithShareLinks = args.canWorkWithShareLinks;
  }

  async request(
    variables: Vars,
    options?: {
      signal?: AbortSignal;
    },
  ) {
    return this._request(variables, options?.signal);
  }

  private async _request(variables: Vars, signal?: AbortSignal) {
    if (!this.canWorkWithShareLinks) {
      this._logger.warn(
        this.operationName +
          ' cannot work with share links! Make sure it is not used in context of share links.',
      );
    }

    this._logger.debug(this.operationName, variables);
    const response = await this._requestFn({
      document: this.document,
      variables,
      signal,
    });

    this._logger.debug(this.operationName, 'succeeded');
    return response;
  }
}
